import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProject } from '../context/ProjectContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave, FiBook } = FiIcons;

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addProject, updateProject, getProject } = useProject();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    genre: '',
    secondary_category: '',
    target_release_date: '',
    word_count: ''
  });

  const [errors, setErrors] = useState({});

  const fictionCategories = [
    'Thriller', 'Fantasy', 'Romance', 'Historical', 
    'Contemporary', 'Biblical Fiction', 'Mystery', 'Sci-Fi',
    'Other'
  ];

  const nonfictionCategories = [
    'Bible Study', 'Devotional', 'Christian Living', 'Business', 'Leadership',
    'Parenting', 'Spiritual Growth', 'Theology', 'Memoir', 'Ministry',
    'Other'
  ];

  useEffect(() => {
    if (isEditing) {
      const project = getProject(id);
      if (project) {
        setFormData({
          title: project.title || '',
          subtitle: project.subtitle || '',
          genre: project.genre || '',
          secondary_category: project.secondary_category || '',
          target_release_date: project.target_release_date || '',
          word_count: project.word_count || ''
        });
      }
    }
  }, [id, isEditing, getProject]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Reset secondary_category if genre changes
      if (name === 'genre') {
        newData.secondary_category = '';
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.genre) {
      newErrors.genre = 'Genre is required';
    }

    if (!formData.secondary_category) {
      newErrors.secondary_category = 'Category is required';
    }
    
    if (formData.word_count && (isNaN(formData.word_count) || parseInt(formData.word_count) < 0)) {
      newErrors.word_count = 'Word count must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const projectData = {
      ...formData,
      // Defaulting to Independent as per requirements
      publishing_path: 'Independent',
      word_count: formData.word_count ? parseInt(formData.word_count) : null
    };
    
    if (isEditing) {
      updateProject(id, projectData);
      navigate(`/project/${id}/checklist`);
    } else {
      const newProjectId = addProject(projectData);
      navigate(`/project/${newProjectId}/checklist`);
    }
  };

  const currentCategories = formData.genre === 'Fiction' 
    ? fictionCategories 
    : formData.genre === 'Nonfiction' 
      ? nonfictionCategories 
      : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to Dashboard
          </Link>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {isEditing ? 'Edit Book Project' : 'Add New Book Project'}
          </h1>
          <p className="text-gray-600">
            {isEditing 
              ? 'Update your project details below.' 
              : 'Start your faithful publishing journey by setting up your book details.'
            }
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                Book Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your book title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Subtitle */}
            <div>
              <label htmlFor="subtitle" className="block text-sm font-semibold text-gray-700 mb-2">
                Subtitle
              </label>
              <input
                type="text"
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your book subtitle (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Genre */}
              <div>
                <label htmlFor="genre" className="block text-sm font-semibold text-gray-700 mb-2">
                  Genre *
                </label>
                <select
                  id="genre"
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.genre ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a genre</option>
                  <option value="Fiction">Fiction</option>
                  <option value="Nonfiction">Nonfiction</option>
                </select>
                {errors.genre && (
                  <p className="mt-1 text-sm text-red-600">{errors.genre}</p>
                )}
              </div>

              {/* Secondary Category - Dependent on Genre */}
              <div>
                <label htmlFor="secondary_category" className={`block text-sm font-semibold mb-2 ${!formData.genre ? 'text-gray-400' : 'text-gray-700'}`}>
                  Category *
                </label>
                <select
                  id="secondary_category"
                  name="secondary_category"
                  value={formData.secondary_category}
                  onChange={handleInputChange}
                  disabled={!formData.genre}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.secondary_category ? 'border-red-500' : 'border-gray-300'
                  } ${!formData.genre ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {formData.genre ? 'Select a category' : 'Select genre first'}
                  </option>
                  {currentCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {errors.secondary_category && (
                  <p className="mt-1 text-sm text-red-600">{errors.secondary_category}</p>
                )}
              </div>
            </div>

            {/* Target Release Date */}
            <div>
              <label htmlFor="target_release_date" className="block text-sm font-semibold text-gray-700 mb-2">
                Target Release Date
              </label>
              <input
                type="date"
                id="target_release_date"
                name="target_release_date"
                value={formData.target_release_date}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Word Count */}
            <div>
              <label htmlFor="word_count" className="block text-sm font-semibold text-gray-700 mb-2">
                Word Count
              </label>
              <input
                type="number"
                id="word_count"
                name="word_count"
                value={formData.word_count}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.word_count ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter approximate word count"
                min="0"
              />
              {errors.word_count && (
                <p className="mt-1 text-sm text-red-600">{errors.word_count}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center"
              >
                <SafeIcon icon={FiSave} className="mr-2" />
                {isEditing ? 'Update Project' : 'Save Project'}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Inspirational Note */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8 p-6 bg-white/50 rounded-lg"
        >
          <SafeIcon icon={FiBook} className="text-3xl text-blue-600 mx-auto mb-3" />
          <p className="text-blue-700 font-medium italic">
            "Every great book begins with faithful preparation and clear intention."
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectForm;